import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, Package, DollarSign, TrendingUp, Eye, Send } from 'lucide-react';
import api from '../services/api';

export default function OrderManagement() {
  const [orders, setOrders] = useState([]);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, financeRes] = await Promise.all([
        api.get(`/order-admin/orders?status=${filter}`),
        api.get('/order-admin/finance')
      ]);
      if (ordersRes.data.success) setOrders(ordersRes.data.orders);
      if (financeRes.data.success) setFinance(financeRes.data.finance);
    } catch (e) {}
    setLoading(false);
  };

  const approveOrder = async (id) => {
    setActionLoading(id);
    try {
      await api.post(`/order-admin/orders/${id}/approve`);
      fetchData();
    } catch (e) {}
    setActionLoading(null);
  };

  const deliverOrder = async (id) => {
    setActionLoading(id);
    try {
      await api.post(`/order-admin/orders/${id}/deliver`);
      fetchData();
    } catch (e) {}
    setActionLoading(null);
  };

  const cancelOrder = async (id) => {
    if (!window.confirm('Siparişi iptal etmek istediğinize emin misiniz?')) return;
    setActionLoading(id);
    try {
      await api.post(`/order-admin/orders/${id}/cancel`);
      fetchData();
    } catch (e) {}
    setActionLoading(null);
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    paid: 'bg-blue-500/20 text-blue-400',
    delivered: 'bg-green-500/20 text-green-400',
    expired: 'bg-gray-500/20 text-gray-400',
    cancelled: 'bg-red-500/20 text-red-400'
  };

  const statusLabels = {
    pending: 'Bekliyor',
    paid: 'Ödendi',
    delivered: 'Teslim Edildi',
    expired: 'Süresi Doldu',
    cancelled: 'İptal'
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Sipariş Yönetimi</h1>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </button>
      </div>

      {/* Finance Stats */}
      {finance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Package}
            label="Toplam Sipariş"
            value={finance.total_orders}
            color="cyan"
          />
          <StatCard
            icon={CheckCircle}
            label="Teslim Edilen"
            value={finance.delivered}
            color="green"
          />
          <StatCard
            icon={DollarSign}
            label="Toplam Gelir"
            value={`$${finance.total_revenue.toFixed(2)}`}
            color="blue"
          />
          <StatCard
            icon={TrendingUp}
            label="Satılan Data"
            value={finance.total_items_sold}
            color="purple"
          />
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {['all', 'pending', 'paid', 'delivered', 'expired'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              filter === f
                ? 'bg-cyan-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {f === 'all' ? 'Tümü' : statusLabels[f]}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Sipariş</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Adet</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tutar</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Teslimat</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Yükleniyor...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Sipariş bulunamadı
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <div className="text-white font-mono text-sm">{order.order_code}</div>
                      {order.payment_txhash && (
                        <div className="text-gray-500 text-xs mt-1">TX: {order.payment_txhash.slice(0, 12)}...</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white">{order.quantity}</td>
                    <td className="px-4 py-3 text-cyan-400 font-semibold">${order.total_usd}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {order.delivery_type === 'telegram' ? (
                          <>
                            <Send className="w-4 h-4 text-blue-400" />
                            <span className="text-gray-300">@{order.telegram_username}</span>
                          </>
                        ) : (
                          <>
                            <Package className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">İndir</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusColors[order.status]}`}>
                        {statusLabels[order.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {new Date(order.created_at).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {order.status === 'pending' && (
                          <>
                            <button
                              onClick={() => approveOrder(order.id)}
                              disabled={actionLoading === order.id}
                              className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg"
                              title="Onayla"
                            >
                              {actionLoading === order.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => cancelOrder(order.id)}
                              disabled={actionLoading === order.id}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                              title="İptal"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {order.status === 'paid' && (
                          <button
                            onClick={() => deliverOrder(order.id)}
                            disabled={actionLoading === order.id}
                            className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg"
                            title="Teslim Et"
                          >
                            {actionLoading === order.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    cyan: 'from-cyan-500/20 to-blue-500/20 text-cyan-400',
    green: 'from-green-500/20 to-emerald-500/20 text-green-400',
    blue: 'from-blue-500/20 to-indigo-500/20 text-blue-400',
    purple: 'from-purple-500/20 to-pink-500/20 text-purple-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4`}>
      <Icon className="w-6 h-6 mb-2" />
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-gray-400 text-sm">{label}</div>
    </div>
  );
}
